/**
 * The vote that decides which CRM window holds the Twilio registration.
 *
 * React-free on purpose: this is the part that must not be wrong, and it can be
 * tested without a DOM, a BroadcastChannel or a Device. `useDeviceLease.ts` is
 * the wiring - events in, `held` out - and it makes no decisions of its own.
 *
 * The failure this is built to avoid is not "two windows hold the lease" (that is
 * merely the old behaviour, where Twilio picked one) but "NO window holds it".
 * Both sides of a claim run this same comparison on the same two values, so they
 * cannot both conclude that the other one won.
 */

/** A window's bid for the registration: when it last became the active one. */
export interface LeaseClaim {
  /** Stable per window, and the tie-break of last resort. */
  id: string;
  /** Epoch ms of the claim. Newer beats older, because newer means "in use". */
  at: number;
}

/**
 * Does `theirs` beat `mine`?
 *
 * Newest wins. Equal stamps are broken by comparing ids, which is arbitrary but
 * TOTAL and identical on both sides - the property that matters. Without it, two
 * windows claiming in the same millisecond would each see a claim that was "not
 * older than mine", both yield, and the phone would ring nowhere at all.
 */
export function claimWins(mine: LeaseClaim, theirs: LeaseClaim): boolean {
  if (theirs.id === mine.id) return false;
  if (theirs.at !== mine.at) return theirs.at > mine.at;
  return theirs.id > mine.id;
}

/**
 * Which of a set of windows ends up holding the lease.
 *
 * Not used at runtime - each window only ever sees the claims that reach it and
 * decides for itself with `claimWins`. It exists so a test can assert the
 * property that emerges from those independent decisions: whatever order the
 * messages arrive in, exactly one window is left holding it.
 */
export function leaseWinner(claims: readonly LeaseClaim[]): LeaseClaim | null {
  return claims.reduce<LeaseClaim | null>(
    (best, claim) => (best === null || claimWins(best, claim) ? claim : best),
    null,
  );
}
